import { type OperationalEventAction } from '../analytics-client';

import { ERROR_EVENT_MAX_PER_SESSION, ERROR_EVENT_THROTTLE_INTERVAL_MS } from './constants';

interface ThrottleEntry {
	lastSentAt: number;
	sentCount: number;
}

// Per-process (per page load) throttle state, keyed by operational event action.
// Resets naturally on page reload because module state is re-initialised, which gives
// us the "per session" semantic for free (same pattern as bsc-global-state).
const throttleState = new Map<OperationalEventAction, ThrottleEntry>();

/**
 * Returns true (and records the send) if an operational error event for `action` may be sent now;
 * returns false if it should be dropped. Throttling rules, applied independently per `action`:
 *  - at most one send per ERROR_EVENT_THROTTLE_INTERVAL_MS, and
 *  - at most ERROR_EVENT_MAX_PER_SESSION sends per page load.
 */
export function shouldSendErrorEvent(action: OperationalEventAction): boolean {
	const now = Date.now();
	const entry = throttleState.get(action);

	if (!entry) {
		// First occurrence of this action this session always sends.
		throttleState.set(action, { lastSentAt: now, sentCount: 1 });
		return true;
	}

	if (entry.sentCount >= ERROR_EVENT_MAX_PER_SESSION) {
		// Hard per-session cap reached — drop regardless of elapsed time.
		return false;
	}

	if (now - entry.lastSentAt < ERROR_EVENT_THROTTLE_INTERVAL_MS) {
		// Within the cooldown window since the last send for this action.
		return false;
	}

	entry.lastSentAt = now;
	entry.sentCount += 1;
	return true;
}

// Test-only helper to reset the per-process throttle state between tests.
export function __resetErrorEventThrottleState(): void {
	throttleState.clear();
}
